import type { GiftCardWalletLink } from '../utils/gift-card-wallet-link';
import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type {
  CreateGiftCard,
  GiftCard,
  GiftCardFilters,
  GiftCardPage,
} from '../types/gift-card.types';

const tag = API_TAGS.GiftCards;
export const giftCardsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    getGiftCardWalletLink: b.mutation<
      GiftCardWalletLink,
      { branchCode: string; code: string }
    >({
      query: ({ branchCode, code }) => ({
        url: '/wallet-links',
        method: 'POST',
        body: { branchCode, kind: 'GIFT_CARD', code },
      }),
      transformResponse: normalizePro20Response<GiftCardWalletLink>,
    }),
    getGiftCards: b.query<GiftCardPage, GiftCardFilters>({
      query: (params) => ({ url: '/gift-cards', params }),
      transformResponse: normalizePro20Response<GiftCardPage>,
      providesTags: (_result, _error, { branchCode }) => [
        { type: tag, id: `LIST-${branchCode}` },
      ],
    }),
    getGiftCard: b.query<GiftCard, { branchCode: string; code: string }>({
      query: ({ branchCode, code }) => ({
        url: `/gift-cards/${encodeURIComponent(code)}`,
        params: { branchCode },
      }),
      transformResponse: normalizePro20Response<GiftCard>,
      providesTags: (_result, _error, { branchCode, code }) => [
        { type: tag, id: `${branchCode}:${code}` },
      ],
    }),
    createGiftCard: b.mutation<
      GiftCard,
      { branchCode: string; body: CreateGiftCard }
    >({
      query: ({ branchCode, body }) => ({
        url: '/gift-cards',
        method: 'POST',
        params: { branchCode },
        body,
      }),
      transformResponse: normalizePro20Response<GiftCard>,
      invalidatesTags: (_result, error, { branchCode }) =>
        error ? [] : [{ type: tag, id: `LIST-${branchCode}` }],
    }),
    emailGiftCard: b.mutation<
      { detail: string },
      { branchCode: string; code: string }
    >({
      query: ({ branchCode, code }) => ({
        url: `/gift-cards/${encodeURIComponent(code)}/email`,
        method: 'POST',
        params: { branchCode },
      }),
      transformResponse: normalizePro20Response<{ detail: string }>,
    }),
  }),
});
export const {
  useGetGiftCardWalletLinkMutation,
  useGetGiftCardsQuery,
  useGetGiftCardQuery,
  useCreateGiftCardMutation,
  useEmailGiftCardMutation,
} = giftCardsApi;
